/**
 * PivotPoints — Professional Trading Dashboard
 * 
 * Features:
 * - Card-based signal display with complete trading params
 * - Latest vs History mode separation
 * - Real-time quality metrics
 * - Mobile-optimized responsive design
 * - Risk-first presentation
 * - Full i18n support (vi/en)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Button, CircularProgress, alpha, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Drawer,
  IconButton, Skeleton,
} from '@mui/material';
import {
  TrackChanges, Refresh, ShoppingCart, Visibility, RemoveRedEye, Block,
  Close, TrendingUp, Warning, CheckCircle, Assessment, Speed, Timeline,
  Today, History, Flag, ShowChart, GpsFixed, FileDownloadOutlined,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import html2canvas from 'html2canvas';

// ═══════════════════════════════════════════════════════════════
// DESIGN TOKENS
// ═══════════════════════════════════════════════════════════════

// Pivot type visual config
const PIVOT_TYPE_COLORS = {
  BOUNCE: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  REVERSAL: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  ACCUMULATION: { bg: '#faf5ff', color: '#9333ea', border: '#e9d5ff' },
};

const LEVEL_META = {
  quality: {
    excellent: { key: 'pivotPoints.quality.excellent', color: '#10b981' },
    good: { key: 'pivotPoints.quality.good', color: '#3b82f6' },
    fair: { key: 'pivotPoints.quality.fair', color: '#f59e0b' },
    poor: { key: 'pivotPoints.quality.poor', color: '#94a3b8' },
  },
  score: {
    veryStrong: { key: 'pivotPoints.levels.score.veryStrong', color: '#10b981' },
    strong: { key: 'pivotPoints.levels.score.strong', color: '#3b82f6' },
    fair: { key: 'pivotPoints.levels.score.fair', color: '#f59e0b' },
    average: { key: 'pivotPoints.levels.score.average', color: '#f97316' },
    weak: { key: 'pivotPoints.levels.score.weak', color: '#ef4444' },
  },
  trap: {
    verySafe: { key: 'pivotPoints.levels.trap.verySafe', color: '#10b981' },
    safe: { key: 'pivotPoints.levels.trap.safe', color: '#3b82f6' },
    caution: { key: 'pivotPoints.levels.trap.caution', color: '#f59e0b' },
    highRisk: { key: 'pivotPoints.levels.trap.highRisk', color: '#ef4444' },
  },
  volume: {
    surge: { key: 'pivotPoints.levels.volume.surge', color: '#10b981' },
    positive: { key: 'pivotPoints.levels.volume.positive', color: '#3b82f6' },
    normal: { key: 'pivotPoints.levels.volume.normal', color: '#f59e0b' },
    low: { key: 'pivotPoints.levels.volume.low', color: '#ef4444' },
  },
  rs: {
    outperform: { key: 'pivotPoints.levels.rs.outperform', color: '#10b981' },
    stronger: { key: 'pivotPoints.levels.rs.stronger', color: '#3b82f6' },
    neutral: { key: 'pivotPoints.levels.rs.neutral', color: '#f59e0b' },
    weaker: { key: 'pivotPoints.levels.rs.weaker', color: '#ef4444' },
  },
  base: {
    strong: { key: 'pivotPoints.levels.base.strong', color: '#15803d', bg: alpha('#10b981', 0.1) },
    fair: { key: 'pivotPoints.levels.base.fair', color: '#1e40af', bg: alpha('#3b82f6', 0.1) },
    weak: { key: 'pivotPoints.levels.base.weak', color: '#64748b', bg: alpha('#94a3b8', 0.1) },
  },
  flow: {
    buyDominant: { key: 'pivotPoints.levels.flow.buyDominant', color: '#10b981' },
    buyingBetter: { key: 'pivotPoints.levels.flow.buyingBetter', color: '#3b82f6' },
    balanced: { key: 'pivotPoints.levels.flow.balanced', color: '#f59e0b' },
    sellDominant: { key: 'pivotPoints.levels.flow.sellDominant', color: '#ef4444' },
  },
  ma20: {
    farAbove: { key: 'pivotPoints.levels.ma20.farAbove', color: '#f59e0b' },
    above: { key: 'pivotPoints.levels.ma20.above', color: '#10b981' },
    near: { key: 'pivotPoints.levels.ma20.near', color: '#3b82f6' },
    below: { key: 'pivotPoints.levels.ma20.below', color: '#ef4444' },
  },
  riskReward: {
    optimized: { color: '#10b981', icon: GpsFixed },
    good: { color: '#3b82f6', icon: CheckCircle },
    caution: { color: '#f59e0b', icon: Warning },
  },
};

const getLevelMeta = (group, levelKey, t) => {
  const fallback = { text: '—', color: '#94a3b8' };
  if (!levelKey) return fallback;
  const config = LEVEL_META[group]?.[levelKey];
  if (!config) return fallback;
  return {
    text: t(config.key),
    color: config.color,
    bg: config.bg,
  };
};

const getFlowTooltipDesc = (flowLevelKey, t) => {
  if (flowLevelKey === 'buyDominant' || flowLevelKey === 'buyingBetter') {
    return t('pivotPoints.tooltips.buyingPressure');
  }
  if (flowLevelKey === 'balanced') {
    return t('pivotPoints.tooltips.balanced');
  }
  return t('pivotPoints.tooltips.sellingPressure');
};

const getRiskRewardMeta = (levelKey) => LEVEL_META.riskReward[levelKey] || LEVEL_META.riskReward.caution;

const fmtPrice = (value, locale = 'en-US') => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(locale, { maximumFractionDigits: 0 });
};

const fmtDateLabel = (value, locale = 'en-US') => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
};

/* Label-value row with human level text */
const LevelRow = ({ label, levelText, levelColor }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.35 }}>
    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: levelColor }}>
      {levelText}
    </Typography>
  </Box>
);

const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8faff' };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

// ── Stat Tile (Reusable) ────────────────────────────────────
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ 
        width: 38, height: 38, borderRadius: 2, flexShrink: 0, 
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, 
        display: 'flex', alignItems: 'center', justifyContent: 'center', 
        boxShadow: `0 3px 8px ${alpha(color, 0.25)}` 
      }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: 'monospace' }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

const MetricStack = ({ primary, secondary, color, align = 'center' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: align, lineHeight: 1.1 }}>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color }}>
      {primary}
    </Typography>
    {secondary ? (
      <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#94a3b8', mt: 0.35, fontFamily: 'monospace' }}>
        {secondary}
      </Typography>
    ) : null}
  </Box>
);

// ── Section Header ───────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
// SIGNAL DETAIL DRAWER — Right side panel
// ═══════════════════════════════════════════════════════════════

// Compact label-value row used across dialog
const DRow = ({ label, value, color, mono = true, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.35 }}>
    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: bold ? 800 : 700, color: color || '#1e293b', fontFamily: mono ? 'monospace' : 'inherit' }}>
      {value}
    </Typography>
  </Box>
);

const getMonitorStageMeta = (stage, t) => {
  const cfg = {
    triggered: { color: '#0f766e' },
    near_trigger: { color: '#2563eb' },
    improving: { color: '#7c3aed' },
    forming: { color: '#475569' },
    degrading: { color: '#dc2626' },
    neutral: { color: '#94a3b8' },
  };
  return {
    text: t(`pivotPoints.dialog.monitorStages.${stage || 'neutral'}`),
    color: cfg[stage || 'neutral']?.color || '#94a3b8',
  };
};

const fmtSignedDelta = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || Math.abs(num) < 0.5) return null;
  return `${num > 0 ? '+' : ''}${num.toFixed(0)}`;
};

const getPivotPresenceMeta = ({ hasPivot, actionLabel }, t) => {
  if (!hasPivot) {
    return {
      title: t('pivotPoints.dialog.pivotPresence.forming'),
      desc: t('pivotPoints.dialog.pivotDecision.NO_PIVOT'),
      color: '#64748b',
    };
  }

  const byAction = {
    ACTION_BUY_NOW: {
      title: t('pivotPoints.dialog.pivotPresence.triggered'),
      desc: t('pivotPoints.dialog.pivotDecision.ACTION_BUY_NOW'),
      color: '#0f766e',
    },
    ACTION_WATCHLIST: {
      title: t('pivotPoints.dialog.pivotPresence.triggered'),
      desc: t('pivotPoints.dialog.pivotDecision.ACTION_WATCHLIST'),
      color: '#2563eb',
    },
    ACTION_OBSERVE: {
      title: t('pivotPoints.dialog.pivotPresence.triggered'),
      desc: t('pivotPoints.dialog.pivotDecision.ACTION_OBSERVE'),
      color: '#7c3aed',
    },
    ACTION_AVOID_TRAP: {
      title: t('pivotPoints.dialog.pivotPresence.triggered'),
      desc: t('pivotPoints.dialog.pivotDecision.ACTION_AVOID_TRAP'),
      color: '#dc2626',
    },
  };

  return byAction[actionLabel] || {
    title: t('pivotPoints.dialog.pivotPresence.triggered'),
    desc: t('pivotPoints.dialog.pivotDecision.ACTION_OBSERVE'),
    color: '#475569',
  };
};

const SignalHistoryTrend = ({ rows, locale, t }) => {
  if (!rows.length) return null;

  const chartRows = [...rows]
    .slice()
    .reverse()
    .filter((row) => row?.date);

  if (chartRows.length < 2) return null;

  const trapMax = Math.max(18, ...chartRows.map((row) => Number(row.trap_risk_score) || 0));
  const chartData = chartRows.map((row) => ({
    date: fmtDateLabel(row.date, locale),
    entryScore: Number(row.entry_quality_score) || 0,
    trapScoreScaled: ((Number(row.trap_risk_score) || 0) / trapMax) * 100,
  }));

  return (
    <Box sx={{ mb: 1.1 }}>
      <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b', mb: 0.7 }}>
        {t('pivotPoints.dialog.monitorTrend')}
      </Typography>
      <Box sx={{ borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #eef2f7', px: 0.8, pt: 0.8, pb: 0.6 }}>
        <Box sx={{ width: '100%', height: 128 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 100]} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(value, name) => [
                  `${Number(value).toFixed(0)}`,
                  name === 'entryScore' ? t('pivotPoints.dialog.monitorTrendEntry') : t('pivotPoints.dialog.monitorTrendTrap'),
                ]}
              />
              <Line
                type="monotone"
                dataKey="entryScore"
                stroke="#3b82f6"
                strokeWidth={2.5}
                dot={{ r: 2.5, strokeWidth: 0, fill: '#3b82f6' }}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="trapScoreScaled"
                stroke="#f59e0b"
                strokeWidth={2.5}
                dot={{ r: 2.5, strokeWidth: 0, fill: '#f59e0b' }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap', px: 0.2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
            <Box sx={{ width: 10, height: 2.5, borderRadius: 999, bgcolor: '#3b82f6' }} />
            <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{t('pivotPoints.dialog.monitorTrendEntry')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
            <Box sx={{ width: 10, height: 2.5, borderRadius: 999, bgcolor: '#f59e0b' }} />
            <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{t('pivotPoints.dialog.monitorTrendTrap')}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const SignalDetailDrawer = ({ open, onClose, onExited, signal, ACTION_CFG, t, i18n }) => {
  const [retainedSignal, setRetainedSignal] = useState(signal);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    if (signal) {
      setRetainedSignal(signal);
    }
  }, [signal]);

  const activeSignal = signal || retainedSignal;
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const locale = lang === 'vi' ? 'vi-VN' : 'en-US';
  const action = ACTION_CFG[activeSignal?.action_label] || ACTION_CFG.ACTION_OBSERVE;
  const qLevel = getLevelMeta('quality', activeSignal?.entry_quality_level, t);
  const riskRewardMeta = getRiskRewardMeta(activeSignal?.risk_reward_level);
  const pivotScore = getLevelMeta('score', activeSignal?.pivot_score_level, t);
  const trap = getLevelMeta('trap', activeSignal?.trap_risk_level, t);
  const base = getLevelMeta('base', activeSignal?.base_quality_level, t);
  const marketResistance = getLevelMeta('score', activeSignal?.market_resistance_level, t);
  const sectorAlignment = getLevelMeta('score', activeSignal?.sector_alignment_level, t);
  const volume = getLevelMeta('volume', activeSignal?.volume_level, t);
  const flow = getLevelMeta('flow', activeSignal?.flow_level, t);
  const rs3 = getLevelMeta('rs', activeSignal?.rs_3_level, t);
  const rs5 = getLevelMeta('rs', activeSignal?.rs_5_level, t);
  const rs20 = getLevelMeta('rs', activeSignal?.rs_20_level, t);
  const sectorRs5 = getLevelMeta('rs', activeSignal?.sector_rs_5_level, t);
  const sectorRs20 = getLevelMeta('rs', activeSignal?.sector_rs_20_level, t);
  const distMa20 = getLevelMeta('ma20', activeSignal?.dist_ma20_level, t);
  const typeKey = activeSignal?.pivot_type?.replace('_PIVOT', '') || 'N/A';
  const typeTone = PIVOT_TYPE_COLORS[typeKey] || { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' };
  const typeLabel = t(`pivotPoints.pivotTypes.${typeKey}`, typeKey);
  const pivotPresence = getPivotPresenceMeta({
    hasPivot: Boolean(activeSignal?.pivot_type),
    actionLabel: activeSignal?.action_label,
  }, t);
  
  const entry = activeSignal?.close_price || activeSignal?.close;
  
  // Backend calculated params (optimized by backtest)
  const stop = activeSignal?.recommended_stop;
  const stopPct = activeSignal?.recommended_stop_pct;
  const t1 = activeSignal?.recommended_target_1;
  const t1Pct = activeSignal?.recommended_target_1_pct;
  const t2 = activeSignal?.recommended_target_2;
  const t2Pct = activeSignal?.recommended_target_2_pct;
  const riskReward = activeSignal?.risk_reward_ratio;
  const companyName = activeSignal?.company_name || activeSignal?.symbol || activeSignal?.ticker || t('pivotPoints.dialog.na');
  const sectorName = activeSignal?.sector
    ? (typeof activeSignal.sector === 'object'
      ? activeSignal.sector[lang] || activeSignal.sector.en || activeSignal.sector.vi || t('pivotPoints.dialog.na')
      : activeSignal.sector)
    : t('pivotPoints.dialog.na');
  
  // Simple position size (user can adjust)
  const posSize = Math.floor(10000000 / entry) * 100;

  const handleExport = useCallback(async () => {
    if (!captureRef.current || exporting || !activeSignal?.symbol) return;
    setExporting(true);
    const node = captureRef.current;
    const scrollEl = node.querySelector('[data-capture-scroll]');
    const prevNode = {
      height: node.style.height,
      maxHeight: node.style.maxHeight,
    };
    const prev = scrollEl ? {
      overflow: scrollEl.style.overflow,
      height: scrollEl.style.height,
      maxHeight: scrollEl.style.maxHeight,
    } : null;

    if (scrollEl) {
      scrollEl.style.overflow = 'visible';
      scrollEl.style.height = 'auto';
      scrollEl.style.maxHeight = 'none';
    }
    node.style.height = 'auto';
    node.style.maxHeight = 'none';

    const ignored = Array.from(node.querySelectorAll('[data-html2canvas-ignore="true"]'));
    const ignoredPrev = ignored.map((el) => ({ el, display: el.style.display }));
    ignored.forEach((el) => { el.style.display = 'none'; });

    const fullwidth = Array.from(node.querySelectorAll('[data-export-fullwidth="true"]'));
    const fullwidthPrev = fullwidth.map((el) => ({
      el,
      maxWidth: el.style.maxWidth,
      overflow: el.style.overflow,
      textOverflow: el.style.textOverflow,
      whiteSpace: el.style.whiteSpace,
    }));
    fullwidth.forEach((el) => {
      el.style.maxWidth = 'none';
      el.style.overflow = 'visible';
      el.style.textOverflow = 'clip';
      el.style.whiteSpace = 'normal';
    });

    const softBg = Array.from(node.querySelectorAll('[data-export-soft-bg="true"]'));
    const softBgPrev = softBg.map((el) => ({
      el,
      background: el.style.background,
      backgroundColor: el.style.backgroundColor,
      boxShadow: el.style.boxShadow,
      borderColor: el.style.borderColor,
    }));
    softBg.forEach((el) => {
      el.style.background = '#ffffff';
      el.style.backgroundColor = '#ffffff';
      el.style.boxShadow = 'none';
      el.style.borderColor = 'rgba(226, 232, 240, 0.9)';
    });

    await new Promise((resolve) => setTimeout(resolve, 80));
    try {
      const rect = node.getBoundingClientRect();
      const captureHeight = Math.ceil(rect.height);
      const canvas = await html2canvas(node, {
        backgroundColor: '#fafbfc',
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
        windowHeight: captureHeight,
        height: captureHeight,
        width: node.scrollWidth,
      });
      const link = document.createElement('a');
      const dateStr = String(activeSignal?.date || new Date().toISOString().slice(0, 10));
      link.download = `PIVOT_${activeSignal.symbol}_${dateStr}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Pivot export failed:', error);
    } finally {
      ignoredPrev.forEach(({ el, display }) => { el.style.display = display; });
      fullwidthPrev.forEach(({ el, maxWidth, overflow, textOverflow, whiteSpace }) => {
        el.style.maxWidth = maxWidth;
        el.style.overflow = overflow;
        el.style.textOverflow = textOverflow;
        el.style.whiteSpace = whiteSpace;
      });
      softBgPrev.forEach(({ el, background, backgroundColor, boxShadow, borderColor }) => {
        el.style.background = background;
        el.style.backgroundColor = backgroundColor;
        el.style.boxShadow = boxShadow;
        el.style.borderColor = borderColor;
      });
      if (scrollEl && prev) {
        scrollEl.style.overflow = prev.overflow;
        scrollEl.style.height = prev.height;
        scrollEl.style.maxHeight = prev.maxHeight;
      }
      node.style.height = prevNode.height;
      node.style.maxHeight = prevNode.maxHeight;
      setExporting(false);
    }
  }, [activeSignal?.date, activeSignal?.symbol, exporting]);

  useEffect(() => {
    if (!activeSignal?.symbol) {
      setHistoryRows([]);
      setHistoryLoading(false);
      return;
    }

    let active = true;
    setHistoryLoading(true);

    fetch(`/api/pivot-points/monitor/${activeSignal.symbol}?days=40&anchor_date=${encodeURIComponent(activeSignal.date || '')}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load history');
        return res.json();
      })
      .then((rows) => {
        if (!active) return;
        setHistoryRows(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!active) return;
        setHistoryRows([]);
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [activeSignal?.symbol, activeSignal?.date]);

  if (!activeSignal) return null;
  
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlideProps={{
        onExited: () => {
          setRetainedSignal(null);
          onExited?.();
        },
      }}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, p: 0, bgcolor: '#fafbfc' } } }}
    >
      <Box ref={captureRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.5,
        py: 1.6,
        bgcolor: alpha('#ffffff', 0.7),
        borderBottom: '1px solid #eef2f7',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 0.75,
            bgcolor: '#1e293b',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.88rem', lineHeight: 1, letterSpacing: '0.02em' }}>
              {activeSignal.symbol || activeSignal.ticker}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 850,
                  fontSize: '1rem',
                  color: '#0f172a',
                  lineHeight: 1.15,
                  letterSpacing: '-0.015em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
                title={companyName}
              >
                {companyName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, minWidth: 0, flexWrap: 'wrap' }}>
              {sectorName !== t('pivotPoints.dialog.na') && (
                <Box sx={{
                  flexShrink: 0,
                  px: 0.7,
                  py: 0.2,
                  borderRadius: '6px',
                  bgcolor: alpha('#0ea5e9', 0.1),
                  color: '#0369a1',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                }}>
                  {sectorName}
                </Box>
              )}
              <Box sx={{
                flexShrink: 0,
                px: 0.7,
                py: 0.2,
                borderRadius: '6px',
                bgcolor: alpha(action.color, 0.1),
                color: action.color,
                fontSize: '0.62rem',
                fontWeight: 800,
                lineHeight: 1.4,
              }}>
                {action.short}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-html2canvas-ignore="true">
          <Tooltip title={t('pivotPoints.dialog.exportImage')}>
            <span>
              <IconButton
                onClick={handleExport}
                size="small"
                disabled={exporting}
                sx={{
                  border: '1px solid #e2e8f0',
                  bgcolor: '#fff',
                  color: '#475569',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
                }}
              >
                {exporting ? <CircularProgress size={16} /> : <FileDownloadOutlined sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: '1px solid #e2e8f0',
              bgcolor: '#fff',
              color: '#475569',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      
      <Box data-capture-scroll sx={{ p: 0, bgcolor: '#fafbfc', overflowY: 'auto', flex: 1 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 0.6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
            <TrackChanges sx={{ fontSize: 15, color: typeTone.color }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: typeTone.color }}>
              {t('pivotPoints.dialog.setupLens')}
            </Typography>
          </Box>
          <Box
            data-export-soft-bg="true"
            sx={{
            borderRadius: 1.7,
            border: `1px solid ${alpha(typeTone.color, 0.16)}`,
            bgcolor: '#fff',
            background: `linear-gradient(180deg, ${alpha(typeTone.color, 0.05)} 0%, #ffffff 52%)`,
            boxShadow: `0 8px 24px ${alpha(typeTone.color, 0.08)}`,
            p: 1.2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, flexWrap: 'wrap', mb: 0.7 }}>
              <Box sx={{
                px: 0.7,
                py: 0.22,
                borderRadius: '999px',
                bgcolor: alpha(typeTone.color, 0.1),
                color: typeTone.color,
                fontSize: '0.62rem',
                fontWeight: 900,
                lineHeight: 1.4,
              }}>
                {typeLabel}
              </Box>
              <Box sx={{
                px: 0.7,
                py: 0.22,
                borderRadius: '999px',
                bgcolor: alpha(action.color, 0.1),
                color: action.color,
                fontSize: '0.62rem',
                fontWeight: 900,
                lineHeight: 1.4,
              }}>
                {action.short}
              </Box>
              <Box sx={{
                px: 0.7,
                py: 0.22,
                borderRadius: '999px',
                bgcolor: alpha(qLevel.color, 0.1),
                color: qLevel.color,
                fontSize: '0.62rem',
                fontWeight: 900,
                lineHeight: 1.4,
              }}>
                {qLevel.text}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.65, mb: 0.7 }}>
              <GpsFixed sx={{ fontSize: 15, color: pivotPresence.color, mt: '2px', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 900, color: pivotPresence.color, lineHeight: 1.25 }}>
                  {pivotPresence.title}
                </Typography>
                <Typography sx={{ fontSize: '0.69rem', color: '#475569', mt: 0.18, lineHeight: 1.45, fontWeight: 600 }}>
                  {pivotPresence.desc}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              mt: 0.75,
              pt: 0.2,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              columnGap: 1.1,
              rowGap: 0.7,
            }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 800, lineHeight: 1.25 }}>
                  {t('pivotPoints.dialog.trapRisk')}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: trap.color, fontWeight: 900, mt: 0.15, lineHeight: 1.2 }}>
                  {trap.text}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 800, lineHeight: 1.25 }}>
                  {t('pivotPoints.dialog.volumeLiquidity')}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: volume.color, fontWeight: 900, mt: 0.15, lineHeight: 1.2 }}>
                  {volume.text}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 800, lineHeight: 1.25 }}>
                  {t('pivotPoints.dialog.baseQuality')}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: base.color, fontWeight: 900, mt: 0.15, lineHeight: 1.2 }}>
                  {base.text}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Trading Plan: Entry / Stop / Targets inline ── */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
            <Flag sx={{ fontSize: 15, color: '#6366f1' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: '#6366f1' }}>{t('pivotPoints.dialog.tradingPlan')}</Typography>
          </Box>
          
          {/* Price row: Entry → Stop → T1 → T2 */}
          <Box sx={{ 
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1,
            bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.2,
          }}>
            <Box>
              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('pivotPoints.dialog.entry')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#1e293b', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {fmtPrice(entry, locale)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.6rem', color: '#ef4444', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('pivotPoints.dialog.stopLoss')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#dc2626', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {fmtPrice(stop, locale)}
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', color: '#ef4444', fontWeight: 600 }}>
                {stopPct?.toFixed(1)}%
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.6rem', color: '#10b981', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('pivotPoints.dialog.target1')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#059669', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {fmtPrice(t1, locale)}
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', color: '#10b981', fontWeight: 600 }}>
                +{t1Pct?.toFixed(1)}%
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.6rem', color: '#059669', fontWeight: 700, textTransform: 'uppercase' }}>
                {t('pivotPoints.dialog.target2')}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#047857', fontFamily: 'monospace', lineHeight: 1.3 }}>
                {fmtPrice(t2, locale)}
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', color: '#059669', fontWeight: 600 }}>
                +{t2Pct?.toFixed(1)}%
              </Typography>
            </Box>
          </Box>
          
          {/* Position & R:R inline */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.8, px: 0.3 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>
              {t('pivotPoints.dialog.positionSize')}: <b style={{ color: '#1e293b' }}>{fmtPrice(posSize, locale)} {t('pivotPoints.dialog.shares')}</b>
              <span style={{ color: '#94a3b8' }}> ≈ {(posSize * entry / 1e6).toFixed(1)}M</span>
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 0.3 }}>
              {t('pivotPoints.dialog.riskRewardFormat', { ratio: riskReward.toFixed(2) })}
              {(() => {
                const RiskRewardIcon = riskRewardMeta.icon;
                return <RiskRewardIcon sx={{ fontSize: 13, color: riskRewardMeta.color }} />;
              })()}
            </Typography>
          </Box>
        </Box>

        {/* ── Stacked sections ── */}
        <Box sx={{ px: 0, pt: 0.4 }}>
          {/* Quality & Volume */}
          <Box sx={{ px: 2, py: 1.2 }}>
            {/* Quality Scores */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
              <Speed sx={{ fontSize: 14, color: '#8b5cf6' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#8b5cf6' }}>{t('pivotPoints.dialog.qualityMetrics')}</Typography>
            </Box>

            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.8 }}>
                <Box>
                  <DRow label={t('pivotPoints.dialog.entryQuality')} value={qLevel.text} color={qLevel.color} bold mono={false} />
                  <DRow label={t('pivotPoints.dialog.trapRisk')} value={trap.text} color={trap.color} bold mono={false} />
                  <DRow label={t('pivotPoints.dialog.marketResist')} value={marketResistance.text} color={marketResistance.color} mono={false} />
                </Box>
                <Box>
                  <DRow label={t('pivotPoints.dialog.pivotScore')} value={pivotScore.text} color={pivotScore.color} bold mono={false} />
                  <DRow label={t('pivotPoints.dialog.baseQuality')} value={base.text} color={base.color} mono={false} />
                  <DRow label={t('pivotPoints.dialog.sectorAlign')} value={sectorAlignment.text} color={sectorAlignment.color} mono={false} />
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 1.1 }} />

            {/* Volume */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
              <Timeline sx={{ fontSize: 14, color: '#3b82f6' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#3b82f6' }}>{t('pivotPoints.dialog.volumeLiquidity')}</Typography>
            </Box>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.8 }}>
                <Box>
                  <LevelRow label={t('pivotPoints.dialog.volumeRatio')} levelText={volume.text} levelColor={volume.color} />
                </Box>
                <Box>
                  <DRow label={t('pivotPoints.dialog.avgLiquidity')} value={`${((activeSignal.vvalue20 || 0) / 1e9).toFixed(1)}B`} />
                </Box>
              </Box>
              <Box sx={{ pt: 0.55 }}>
                <LevelRow label={t('pivotPoints.dialog.upDownRatio')} levelText={flow.text} levelColor={flow.color} />
              </Box>
            </Box>
          </Box>

          {/* Relative Strength & Technical */}
          <Box sx={{ px: 2, py: 1.2 }}>
            {/* Relative Strength */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
              <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#10b981' }}>{t('pivotPoints.dialog.relativeStrength')}</Typography>
            </Box>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.8 }}>
                <Box>
                  <LevelRow label={t('pivotPoints.dialog.rs3d')} levelText={rs3.text} levelColor={rs3.color} />
                  <LevelRow label={t('pivotPoints.dialog.rs20d')} levelText={rs20.text} levelColor={rs20.color} />
                  <LevelRow label={t('pivotPoints.dialog.sectorRs20')} levelText={sectorRs20.text} levelColor={sectorRs20.color} />
                </Box>
                <Box>
                  <LevelRow label={t('pivotPoints.dialog.rs5d')} levelText={rs5.text} levelColor={rs5.color} />
                  <LevelRow label={t('pivotPoints.dialog.sectorRs5')} levelText={sectorRs5.text} levelColor={sectorRs5.color} />
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 1.1 }} />

            {/* Price Structure */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
              <ShowChart sx={{ fontSize: 14, color: '#f59e0b' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#f59e0b' }}>{t('pivotPoints.dialog.priceStructure')}</Typography>
            </Box>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.8 }}>
                <Box>
                  <DRow label={t('pivotPoints.dialog.pivotPrice')} value={fmtPrice(activeSignal.pivot_price, locale)} />
                  <DRow label={t('pivotPoints.dialog.regime')} value={t(`pivotPoints.regime.${activeSignal.market_regime_proxy || 'na'}`)} mono={false} />
                </Box>
                <Box>
                  <LevelRow label={t('pivotPoints.dialog.distMa20')} levelText={distMa20.text} levelColor={distMa20.color} />
                  <DRow label={t('pivotPoints.dialog.signalDate')} value={new Date(activeSignal.date).toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US')} mono={false} />
                </Box>
              </Box>
              <Box sx={{ pt: 0.55 }}>
                <DRow label={t('pivotPoints.dialog.sector')} value={sectorName} mono={false} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 2, py: 1.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
              <ShowChart sx={{ fontSize: 14, color: '#0f766e' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#0f766e' }}>
                {t('pivotPoints.dialog.monitorTrendTitle')}
              </Typography>
            </Box>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.2 }}>
              {historyLoading ? (
                <Skeleton variant="rounded" height={168} sx={{ borderRadius: 1.5 }} />
              ) : historyRows.length === 0 ? (
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  {t('pivotPoints.dialog.monitorHistoryEmpty')}
                </Typography>
              ) : (
                <SignalHistoryTrend rows={historyRows} locale={locale} t={t} />
              )}
            </Box>
          </Box>

          <Box sx={{ px: 2, py: 1.2 }} data-html2canvas-ignore="true">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
              <History sx={{ fontSize: 14, color: '#0f766e' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#0f766e' }}>
                {t('pivotPoints.dialog.monitorHistory')}
              </Typography>
            </Box>
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.2 }}>
              {historyLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                  {[0, 1, 2, 3].map((item) => (
                    <Skeleton key={item} variant="rounded" height={36} sx={{ borderRadius: 1.5 }} />
                  ))}
                </Box>
              ) : historyRows.length === 0 ? (
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  {t('pivotPoints.dialog.monitorHistoryEmpty')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                  {historyRows.slice(0, 14).map((row, index) => {
                    const rowAction = ACTION_CFG[row.action_label] || ACTION_CFG.ACTION_OBSERVE;
                    const rowStage = getMonitorStageMeta(row.monitor_stage, t);
                    const rowQ = getLevelMeta('quality', row.entry_quality_level, t);
                    const rowTrap = getLevelMeta('trap', row.trap_risk_level, t);
                    const rowFlow = getLevelMeta('flow', row.flow_level, t);
                    const eqDelta = fmtSignedDelta(row.entry_quality_delta);

                    return (
                      <Box key={`${row.date}-${row.id || index}`} sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start' }}>
                        <Box sx={{ minWidth: 52, pt: 0.1 }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#0f172a' }}>
                            {fmtDateLabel(row.date, locale)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.2 }}>
                            {row.date === historyRows[0]?.date ? t('pivotPoints.dialog.monitorHistoryCurrent') : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: rowStage.color }}>
                              {rowStage.text}
                            </Typography>
                            {!!row.action_label && !!row.pivot_type && (
                              <>
                                <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1' }}>•</Typography>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: rowAction.color }}>
                                  {rowAction.short}
                                </Typography>
                              </>
                            )}
                            <Typography sx={{ fontSize: '0.65rem', color: rowQ.color, fontWeight: 700 }}>
                              {rowQ.text}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1' }}>•</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: rowTrap.color, fontWeight: 700 }}>
                              {rowTrap.text}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.3 }}>
                            {rowFlow.text} · {t(`pivotPoints.regime.${row.market_regime_proxy || 'na'}`)}
                            {eqDelta ? ` · EQ ${eqDelta}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>
    </Drawer>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const PivotPoints = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('vi') ? 'vi-VN' : 'en-US';
  
  // Action configuration (depends on t())
  const ACTION_CFG = useMemo(() => ({
    ACTION_BUY_NOW: {
      label: t('pivotPoints.actions.ACTION_BUY_NOW'),
      short: t('pivotPoints.filters.buyNow'),
      color: '#10b981',
      light: '#d1fae5',
      icon: ShoppingCart,
      desc: t('pivotPoints.actions.ACTION_BUY_NOW'),
    },
    ACTION_WATCHLIST: {
      label: t('pivotPoints.actions.ACTION_WATCHLIST'),
      short: t('pivotPoints.filters.watchlist'),
      color: '#3b82f6',
      light: '#dbeafe',
      icon: Visibility,
      desc: t('pivotPoints.actions.ACTION_WATCHLIST'),
    },
    ACTION_OBSERVE: {
      label: t('pivotPoints.actions.ACTION_OBSERVE'),
      short: t('pivotPoints.filters.observe'),
      color: '#8b5cf6',
      light: '#f5f3ff',
      icon: RemoveRedEye,
      desc: t('pivotPoints.actions.ACTION_OBSERVE'),
    },
    ACTION_AVOID_TRAP: {
      label: t('pivotPoints.actions.ACTION_AVOID_TRAP'),
      short: t('pivotPoints.actions.ACTION_AVOID_TRAP'),
      color: '#ef4444',
      light: '#fee2e2',
      icon: Block,
      desc: t('pivotPoints.actions.ACTION_AVOID_TRAP'),
    },
  }), [t]);
  
  const [allSignals, setAllSignals] = useState([]);
  const [todaySignals, setTodaySignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('today');
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scanDate, setScanDate] = useState(null);

  const handleOpenDrawer = useCallback((signal) => {
    setSelectedSignal(signal);
    window.requestAnimationFrame(() => {
      setDrawerOpen(true);
    });
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleDrawerExited = useCallback(() => {
    setSelectedSignal(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/pivot-points/candidates?limit=200&days=90&min_liquidity=0');
      if (!res.ok) throw new Error(t('pivotPoints.errors.api'));
      
      const data = await res.json();
      const signals = Array.isArray(data) ? data : (data.signals || []);
      
      setAllSignals(signals);
      
      if (signals.length > 0) {
        const latestDate = signals.reduce((max, s) => {
          const d = new Date(s.date);
          return d > max ? d : max;
        }, new Date(0));
        
        const today = signals.filter(s => new Date(s.date).toDateString() === latestDate.toDateString());
        setTodaySignals(today);
        setScanDate(latestDate);
      } else {
        setTodaySignals([]);
      }
      
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const [calculating, setCalculating] = useState(false);
  const calculate = useCallback(async () => {
    setCalculating(true);
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/pivot-points/calculate?lookback_days=180&min_liquidity=0', { method: 'POST' });
      if (!res.ok) throw new Error(t('pivotPoints.errors.calculateApi'));
      const data = await res.json();
      const signals = Array.isArray(data.signals) ? data.signals : [];
      
      // Merge: replace today signals with fresh calculate results, keep history
      if (signals.length > 0) {
        const calcDate = signals[0].date;
        const historySignals = allSignals.filter(s => s.date !== calcDate);
        setAllSignals([...signals, ...historySignals]);
        setTodaySignals(signals);
        setScanDate(new Date(calcDate));
      }
    } catch (err) {
      console.error('Calculate error:', err);
      setError(err.message);
      // Fallback: reload from DB
      await load();
    } finally {
      setCalculating(false);
      setLoading(false);
    }
  }, [allSignals, load, t]);

  const groupedByAction = useMemo(() => {
    const source = viewMode === 'today' ? todaySignals : allSignals;
    const groups = {};
    Object.keys(ACTION_CFG).forEach(k => groups[k] = []);
    
    source.forEach(s => {
      const key = s.action_label || 'ACTION_OBSERVE';
      if (groups[key]) groups[key].push(s);
    });
    
    Object.keys(groups).forEach(k => {
      groups[k].sort((a, b) => (b.entry_quality_score || 0) - (a.entry_quality_score || 0));
    });
    
    return groups;
  }, [todaySignals, allSignals, viewMode, ACTION_CFG]);

  const displaySignals = useMemo(() => {
    if (!activeFilter) {
      return viewMode === 'today' ? todaySignals : allSignals;
    }
    return groupedByAction[activeFilter] || [];
  }, [activeFilter, groupedByAction, todaySignals, allSignals, viewMode]);

  const stats = useMemo(() => {
    const source = viewMode === 'today' ? todaySignals : allSignals;
    return {
      total: source.length,
      highQuality: source.filter(s => s.is_high_quality).length,
      buyNow: groupedByAction.ACTION_BUY_NOW?.length || 0,
      watchlist: groupedByAction.ACTION_WATCHLIST?.length || 0,
      avgQuality: source.length > 0 
        ? (source.reduce((sum, s) => sum + (s.entry_quality_score || 0), 0) / source.length)
        : 0,
    };
  }, [todaySignals, allSignals, viewMode, groupedByAction]);

  if (loading && !allSignals.length) {
    return (
      <Box sx={{ mx: 'auto', p: { xs: 1.5, sm: 2 } }}>
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
            <Box>
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="text" width={120} height={16} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Skeleton variant="rounded" width={180} height={34} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={90} height={34} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>

        {/* Stat tiles skeleton */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5, mb: 2.5 }}>
          {[0,1,2,3,4].map(i => (
            <Box key={i} sx={{ borderRadius: 1.5, border: '1px solid #f1f5f9', bgcolor: '#fff', p: '14px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={60} height={14} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={40} height={32} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Filter chips skeleton */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {[50,80,90,80,70,60].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={24} sx={{ borderRadius: 3 }} />
          ))}
        </Box>

        {/* Table skeleton */}
        <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', bgcolor: '#fff' }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1, bgcolor: '#f8faff', borderBottom: '1px solid #e2e8f0' }}>
            {[50,60,55,55,65,100,55,45,40,50,65,70].map((w, i) => (
              <Skeleton key={i} variant="text" width={w} height={16} />
            ))}
          </Box>
          {/* Body rows */}
          {[...Array(8)].map((_, row) => (
            <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9' }}>
              <Skeleton variant="text" width={42} height={18} sx={{ fontWeight: 700 }} />
              <Skeleton variant="rounded" width={58} height={22} sx={{ borderRadius: 3 }} />
              <Skeleton variant="text" width={52} height={16} />
              <Skeleton variant="text" width={48} height={16} sx={{ color: '#ef4444' }} />
              <Skeleton variant="text" width={52} height={16} sx={{ color: '#10b981' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                <Skeleton variant="rounded" width={70} height={6} sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" width={30} height={16} />
              </Box>
              <Skeleton variant="text" width={32} height={16} />
              <Skeleton variant="text" width={36} height={16} />
              <Skeleton variant="rounded" width={20} height={20} sx={{ borderRadius: '50%' }} />
              <Skeleton variant="text" width={36} height={16} />
              <Skeleton variant="rounded" width={58} height={22} sx={{ borderRadius: 3 }} />
              <Skeleton variant="rounded" width={64} height={24} sx={{ borderRadius: 3 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mx: 'auto', p: { xs: 1.5, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          }}>
            <TrackChanges sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', color: '#1e293b', lineHeight: 1.1 }}>
              {t('pivotPoints.pageTitle')}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.3 }}>
              {viewMode === 'today'
                ? `${t('pivotPoints.heroMetrics.todaySignals')} • ${scanDate ? new Date(scanDate).toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}`
                : t('pivotPoints.heroMetrics.historyDesc', { count: stats.total })
              }
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, v) => v && setViewMode(v)}
            size="small"
            sx={{ 
              '& .MuiToggleButton-root': { 
                px: 1.5, 
                py: 0.6,
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'none',
                border: '1px solid #e2e8f0',
                '&.Mui-selected': {
                  bgcolor: '#6366f1',
                  color: '#fff',
                  '&:hover': { bgcolor: '#4f46e5' },
                }
              }
            }}
          >
            <ToggleButton value="today">
              <Today sx={{ fontSize: 16, mr: 0.5 }} />
              {t('pivotPoints.viewMode.today')}
              {todaySignals.length > 0 && (
                <Chip 
                  label={todaySignals.length} 
                  size="small"
                  sx={{ ml: 0.8, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                />
              )}
            </ToggleButton>
            <ToggleButton value="history">
              <History sx={{ fontSize: 16, mr: 0.5 }} />
              {t('pivotPoints.viewMode.history')}
              {allSignals.length > 0 && (
                <Chip 
                  label={allSignals.length} 
                  size="small"
                  sx={{ ml: 0.8, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                />
              )}
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Button 
            variant="contained" 
            size="small"
            startIcon={(loading || calculating) ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Refresh />}
            onClick={calculate} 
            disabled={loading || calculating}
            sx={{ 
              fontSize: '0.72rem', 
              textTransform: 'none', 
              bgcolor: '#6366f1', 
              '&:hover': { bgcolor: '#4f46e5' },
              fontWeight: 700,
            }}
          >
            {calculating ? t('pivotPoints.calculating.calculating') : loading ? t('pivotPoints.table.loading') : t('pivotPoints.filters.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5, mb: 2.5 }}>
        <Tile 
          label={t('pivotPoints.heroMetrics.totalSignals')} 
          value={stats.total}
          Icon={Flag}
          color="#6366f1"
        />
        <Tile 
          label={t('pivotPoints.heroMetrics.highQuality')} 
          value={stats.highQuality}
          sub={t('pivotPoints.heroMetrics.eqThreshold')}
          Icon={CheckCircle}
          color="#10b981"
        />
        <Tile 
          label={t('pivotPoints.heroMetrics.buyNow')} 
          value={stats.buyNow}
          sub={t('pivotPoints.heroMetrics.buyNow')}
          Icon={ShoppingCart}
          color="#10b981"
        />
        <Tile 
          label={t('pivotPoints.heroMetrics.watchlist')} 
          value={stats.watchlist}
          sub={t('pivotPoints.heroMetrics.watchlist')}
          Icon={Visibility}
          color="#3b82f6"
        />
        <Tile 
          label={t('pivotPoints.heroMetrics.avgQuality')} 
          value={stats.avgQuality.toFixed(1)}
          Icon={Speed}
          color="#8b5cf6"
        />
      </Box>

      {/* Filter Chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {t('pivotPoints.filters.search').replace('...', '')}:
        </Typography>
        
        <Chip
          label={`${t('pivotPoints.filters.all')} (${viewMode === 'today' ? todaySignals.length : allSignals.length})`}
          onClick={() => setActiveFilter(null)}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            bgcolor: !activeFilter ? '#1e293b' : '#fff',
            color: !activeFilter ? '#fff' : '#64748b',
            border: '1px solid #e2e8f0',
            cursor: 'pointer',
            '&:hover': { bgcolor: !activeFilter ? '#0f172a' : '#f8fafc' },
          }}
        />
        
        {Object.entries(ACTION_CFG).map(([key, cfg]) => {
          const count = groupedByAction[key]?.length || 0;
          const Icon = cfg.icon;
          return (
            <Chip
              key={key}
              icon={<Icon sx={{ fontSize: 14 }} />}
              label={`${cfg.short} (${count})`}
              onClick={() => setActiveFilter(key)}
              size="small"
              disabled={count === 0}
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                bgcolor: activeFilter === key ? cfg.color : '#fff',
                color: activeFilter === key ? '#fff' : cfg.color,
                border: `1px solid ${activeFilter === key ? cfg.color : '#e2e8f0'}`,
                cursor: count > 0 ? 'pointer' : 'not-allowed',
                opacity: count === 0 ? 0.4 : 1,
                '&:hover': count > 0 ? { 
                  bgcolor: activeFilter === key ? cfg.color : cfg.light,
                  borderColor: cfg.color,
                } : {},
              }}
            />
          );
        })}
      </Box>

      {/* Error State */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: alpha('#ef4444', 0.05), border: '1px solid #fca5a5', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ fontSize: 20, color: '#ef4444' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#991b1b', fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Signals Table */}
      {displaySignals.length === 0 ? (
        <Box sx={{ 
          py: 6, 
          px: 3, 
          bgcolor: '#fff',
          border: '1.5px dashed #e2e8f0',
          borderRadius: 2,
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1.5,
        }}>
          <Box sx={{ 
            width: 64, 
            height: 64, 
            borderRadius: '50%', 
            bgcolor: '#f8fafc', 
            border: '2px dashed #cbd5e1', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Assessment sx={{ fontSize: 32, color: '#cbd5e1' }} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#64748b', mb: 0.5 }}>
              {viewMode === 'today' ? t('pivotPoints.emptyState.todayTitle') : t('pivotPoints.emptyState.historyTitle')}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', maxWidth: 400 }}>
              {viewMode === 'today' 
                ? t('pivotPoints.emptyState.todayDesc')
                : t('pivotPoints.emptyState.historyDesc')
              }
            </Typography>
          </Box>
          {viewMode === 'today' && (
            <Button 
              variant="outlined"
              size="small"
              onClick={calculate}
              disabled={calculating || loading}
              startIcon={calculating ? <CircularProgress size={14} /> : null}
              sx={{ 
                mt: 1,
                fontSize: '0.75rem',
                textTransform: 'none',
                fontWeight: 700,
                borderColor: '#6366f1',
                color: '#6366f1',
                '&:hover': { bgcolor: alpha('#6366f1', 0.05), borderColor: '#4f46e5' },
              }}
            >
              {calculating ? t('pivotPoints.calculating.running') : t('pivotPoints.calculating.scanNow')}
            </Button>
          )}
        </Box>
      ) : (
        <TableContainer 
          component={Box}
          sx={{ 
            borderRadius: 2, 
            border: '1px solid #e2e8f0', 
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>{t('pivotPoints.table.headers.ticker')}</TableCell>
                <TableCell sx={thSx}>{t('pivotPoints.table.headers.type')}</TableCell>
                <TableCell sx={thSx} align="right">{t('pivotPoints.table.headers.price')}</TableCell>
                <TableCell sx={thSx} align="right">{t('pivotPoints.dialog.stopLoss')}</TableCell>
                <TableCell sx={thSx} align="right">{t('pivotPoints.dialog.target1')}</TableCell>
                <TableCell sx={thSx} align="center">{t('pivotPoints.table.headers.entryQuality')}</TableCell>
                <TableCell sx={thSx} align="center">{t('pivotPoints.dialog.trapRisk')}</TableCell>
                <TableCell sx={thSx} align="center">{t('pivotPoints.table.headers.vol')}</TableCell>
                <TableCell sx={thSx} align="center">{t('pivotPoints.table.headers.dist')}</TableCell>
                <TableCell sx={thSx} align="center">{t('pivotPoints.table.headers.updown')}</TableCell>
                <TableCell sx={thSx} align="center">{t('pivotPoints.table.headers.regime')}</TableCell>
                <TableCell sx={thSx}>{t('pivotPoints.table.headers.action')}</TableCell>
                {viewMode === 'history' && <TableCell sx={thSx}>{t('pivotPoints.table.headers.date')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {displaySignals.map((s, idx) => {
                const action = ACTION_CFG[s.action_label] || ACTION_CFG.ACTION_OBSERVE;
                const entry = s.close_price;
                const stop = s.recommended_stop;
                const target = s.recommended_target_1;
                const typeKey = s.pivot_type?.replace('_PIVOT', '') || '';
                const rowAccent = PIVOT_TYPE_COLORS[typeKey]?.color || '#e2e8f0';
                const qLevel = getLevelMeta('quality', s.entry_quality_level, t);
                const trap = getLevelMeta('trap', s.trap_risk_level, t);
                const volume = getLevelMeta('volume', s.volume_level, t);
                const base = getLevelMeta('base', s.base_quality_level, t);
                const flow = getLevelMeta('flow', s.flow_level, t);
                
                return (
                  <TableRow 
                    key={s.id || idx} 
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha(rowAccent, 0.04) },
                      '& td:first-of-type': { borderLeft: `3px solid ${alpha(rowAccent, 0.5)}` },
                    }}
                    onClick={() => handleOpenDrawer(s)}
                  >
                    <TableCell sx={{ ...tdSx, fontWeight: 900, fontSize: '0.9rem', color: '#1e293b' }}>
                      {s.symbol}
                    </TableCell>
                    
                    <TableCell sx={tdSx}>
                      {(() => {
                        const typeKey = s.pivot_type?.replace('_PIVOT', '') || 'N/A';
                        const tc = PIVOT_TYPE_COLORS[typeKey] || { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' };
                        const typeLabel = t(`pivotPoints.pivotTypes.${typeKey}`, typeKey);
                        return (
                          <Chip 
                            label={typeLabel} 
                            size="small" 
                            sx={{ 
                              fontSize: '0.65rem', 
                              fontWeight: 700,
                              height: 22,
                              bgcolor: tc.bg,
                              color: tc.color,
                              border: `1px solid ${tc.border}`,
                            }} 
                          />
                        );
                      })()}
                    </TableCell>
                    
                    <TableCell sx={{ ...tdSx, fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }} align="right">
                      {fmtPrice(entry, locale)}
                    </TableCell>
                    
                    <TableCell sx={{ ...tdSx, fontWeight: 800, color: '#dc2626', fontFamily: 'monospace' }} align="right">
                      {fmtPrice(stop, locale)}
                    </TableCell>
                    
                    <TableCell sx={{ ...tdSx, fontWeight: 800, color: '#15803d', fontFamily: 'monospace' }} align="right">
                      {fmtPrice(target, locale)}
                    </TableCell>
                    
                    <TableCell sx={tdSx} align="center">
                      <Box sx={{ minWidth: 84, display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: qLevel.color, lineHeight: 1.2 }}>
                          {qLevel.text}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell sx={tdSx} align="center">
                      <Tooltip title={t('pivotPoints.tooltips.trapRisk', { value: (s.trap_risk_score || 0).toFixed(1) })}>
                        <Box>
                          <MetricStack
                            primary={trap.text}
                            color={trap.color}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell sx={tdSx} align="center">
                      <MetricStack
                        primary={volume.text}
                        color={volume.color}
                      />
                    </TableCell>
                    
                    <TableCell sx={tdSx} align="center">
                      <Tooltip title={t('pivotPoints.tooltips.baseCount', { value: s.distribution_count_15 || 0 })}>
                        <Chip
                          label={base.text}
                          size="small"
                          sx={{ 
                            fontSize: '0.66rem',
                            fontWeight: 800,
                            height: 24,
                            bgcolor: base.bg,
                            color: base.color,
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell sx={tdSx} align="center">
                      <Tooltip title={t('pivotPoints.tooltips.updownRatio', {
                        value: (s.updown_value_ratio_10 || 0).toFixed(2),
                        desc: getFlowTooltipDesc(s.flow_level, t),
                      })}>
                        <Box>
                          <MetricStack
                            primary={flow.text}
                            color={flow.color}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    
                    <TableCell sx={tdSx} align="center">
                      <Chip
                        label={t(`pivotPoints.regime.${s.market_regime_proxy || 'na'}`)}
                        size="small"
                        sx={{ 
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          height: 20,
                          bgcolor: s.market_regime_proxy === 'RISK_ON' ? alpha('#10b981', 0.1) : s.market_regime_proxy === 'SIDEWAY' ? alpha('#f59e0b', 0.1) : alpha('#94a3b8', 0.08),
                          color: s.market_regime_proxy === 'RISK_ON' ? '#15803d' : s.market_regime_proxy === 'SIDEWAY' ? '#92400e' : '#64748b',
                        }}
                      />
                    </TableCell>
                    
                    <TableCell sx={tdSx}>
                      <Chip
                        label={action.short}
                        size="small"
                        sx={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 700,
                          height: 22,
                          bgcolor: alpha(action.color, 0.1),
                          color: action.color,
                          border: `1px solid ${alpha(action.color, 0.2)}`,
                        }}
                      />
                    </TableCell>
                    
                    {viewMode === 'history' && (
                      <TableCell sx={{ ...tdSx, fontSize: '0.7rem', color: '#64748b' }}>
                        {new Date(s.date).toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', { month: '2-digit', day: '2-digit' })}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SignalDetailDrawer 
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onExited={handleDrawerExited}
        signal={selectedSignal}
        ACTION_CFG={ACTION_CFG}
        t={t}
        i18n={i18n}
      />
    </Box>
  );
};

export default PivotPoints;
